import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shell } from '../layout/Shell.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { DriverStatusPill } from '../components/StatusPill.js';
import { approveDriver, listDrivers, type DriverRow } from '../api/admin.js';
import { Avatar } from '../components/Avatar.js';
import { useToast } from '../components/Toast.js';

export function DriversPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const drivers = useQuery({ queryKey: ['admin-drivers'], queryFn: listDrivers, refetchInterval: 15_000 });

  const approval = useMutation({
    mutationFn: ({ id, approved }: { id: string; approved: boolean }) => approveDriver(id, approved),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-drivers'] });
      toast.show(res.approved ? 'Driver approved.' : 'Driver suspended.', res.approved ? 'success' : 'info');
    },
    onError: () => toast.show('Could not update the driver.', 'error'),
  });

  const columns: Column<DriverRow>[] = [
    {
      key: 'name',
      header: 'Driver',
      cell: (d) => (
        <span className="inline-flex items-center gap-2.5">
          <Avatar name={d.user.name} size={28} />
          <span className="font-medium">{d.user.name}</span>
        </span>
      ),
    },
    {
      key: 'vehicle',
      header: 'Vehicle',
      cell: (d) =>
        d.vehicle ? (
          <span>
            {d.vehicle.color} {d.vehicle.make} {d.vehicle.model}
            <span className="ml-2 text-xs text-slate-500">{d.vehicle.plate}</span>
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    { key: 'type', header: 'Type', cell: (d) => <span className="capitalize">{d.vehicle?.type ?? '—'}</span> },
    {
      key: 'rating',
      header: 'Rating',
      align: 'right',
      cell: (d) => <span className="num">{d.rating.toFixed(2)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (d) => <DriverStatusPill status={d.status} approved={d.approved} />,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (d) =>
        d.approved ? (
          <button
            type="button"
            className="btn-ghost text-danger"
            onClick={() => approval.mutate({ id: d.id, approved: false })}
            disabled={approval.isPending}
          >
            Suspend
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary"
            onClick={() => approval.mutate({ id: d.id, approved: true })}
            disabled={approval.isPending}
          >
            Approve
          </button>
        ),
    },
  ];

  return (
    <Shell title="Drivers" subtitle="Approved drivers and pending applications.">
      <DataTable rows={drivers.data ?? []} columns={columns} rowKey={(d) => d.id} loading={drivers.isLoading} />
    </Shell>
  );
}
